import React, { useState, useEffect, useMemo, useRef, useCallback, forwardRef, useImperativeHandle } from "react";

import JsZip from 'jszip';
import FileSaver from 'file-saver';

import ReportStyle from '../css/Report.module.css'

import { API_DEFAULT } from "../services/settings";
import { API } from "../services/routes";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

// The last segment of the path, never of a query string: a signed URL
// carries its signature after "?".
const fileName = (documento) => documento.split("?")[0].split("/").slice(-1)[0];

// tipo_reporte 3 is Modelo 2 (back-ratings #61), which reaches clients on
// 2026-10-01; 0 and 2 are Modelo 1, and 1 is seen only by CNC.
const MODELO_2 = 3;

// What GET /users/reports serves, in the shape the panel draws:
// organisations (one without a name for an ordinary user, one per client for
// CNC), each with its months, each month with its files. Newest first, both
// months and files: what a client comes for is yesterday's file, and the
// panel used to put it at the end of the list.
export const groupReports = (data) => {
  const ofOneOrganisation = (organisation, reports) => {
    const sorted = [...reports].sort((a, b) => {
      const first = new Date(a.fecha_reporte + 'T00:00:00').getTime();
      const second = new Date(b.fecha_reporte + 'T00:00:00').getTime();
      return first === second
        ? fileName(a.documento).localeCompare(fileName(b.documento))
        : second - first;
    });

    const months = [];
    sorted.forEach((report) => {
      const date = new Date(report.fecha_reporte + 'T00:00:00');
      const id = `${organisation || ''}-${date.getFullYear()}-${date.getMonth()}`;
      let month = months.find((m) => m.id === id);
      if (!month) {
        month = { id, label: `${MONTHS[date.getMonth()]} ${date.getFullYear()}`, files: [] };
        months.push(month);
      }
      // documento names the file and keys the selection; the file itself is
      // fetched through a URL the back signs for it (see fetchFile).
      month.files.push({ id: report.id, url: report.documento, name: fileName(report.documento), modelo2: report.tipo_reporte === MODELO_2 });
    });

    return { id: organisation || 'reports', organisation, months };
  };

  if (Array.isArray(data)) {
    return data.length > 0 ? [ofOneOrganisation(null, data)] : [];
  }
  if (data && typeof data === 'object') {
    return Object.keys(data).sort()
      .map((organisation) => ofOneOrganisation(organisation, data[organisation]))
      .filter((group) => group.months.length > 0);
  }
  return [];
};

// The downloadables panel, opened from the button in Viewer's header. What it
// shows, what is open and what is selected are state: until 2026-09-17 they
// were read from and written to the DOM by class name, which lost the
// selection on a repaint and stacked one click listener per paint.
//
// A CNC user gets every organisation's reports: 28,325 of them on
// 2026-09-17, 42,553 on 2026-10-01 with the Modelo 2 history. That rules out drawing what is not open and any check that
// walks a list per file, so closed months draw no files and the selection
// lives in a Set.
const Reports = forwardRef((props, ref) => {

  const { accessToken, setAccessToken, getAccessTokenWithRefresh, setShowLoader, setShowAlert, setMessage, setSeverity, onHasReports } = props;

  const [groups, setGroups] = useState([]);
  const [visible, setVisible] = useState(false);
  const [openSections, setOpenSections] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [fullMonths, setFullMonths] = useState([]);
  const [onlySelected, setOnlySelected] = useState(false);

  // getAccessTokenWithRefresh used to be rebuilt on every render, so a
  // callback that closed over it changed identity every time and its effect
  // asked the back for the reports again, and again. useSession keeps it
  // stable since 2026-09-22; the fetch still reads the props from here, so it
  // does not depend on any of them staying the same.
  // The token the list was asked with, so the effect does not ask again for
  // the same one after a refresh already did.
  const askedWith = useRef(null);
  const latestProps = useRef();
  latestProps.current = { accessToken, setAccessToken, getAccessTokenWithRefresh, onHasReports };

  const alert = (message, severity) => {
    setMessage(message);
    setSeverity(severity);
    setTimeout(() => { setShowAlert(true) }, 0);
  };

  const getReports = useCallback(async (access, afterRefresh = false) => {
    const { setAccessToken, getAccessTokenWithRefresh, onHasReports } = latestProps.current;
    askedWith.current = access;
    let res;
    try {
      res = await fetch(`${API_DEFAULT}/${API.reports}`, {
        headers: { "Authorization": "Bearer " + access }
      });
    } catch (e) {
      return false;
    }

    const data = await res.json().catch(() => undefined);

    if (data && data['code'] === 'token_not_valid') {
      // Asking again with the fresh token is what the user expects from
      // Actualizar: it used to answer that it could not, and work on the
      // second press. Only once, so a token the back keeps refusing does
      // not loop.
      if (afterRefresh) {
        return false;
      }
      const newAccess = await getAccessTokenWithRefresh();
      if (!newAccess) {
        return false;
      }
      setAccessToken(newAccess);
      localStorage.setItem('accessToken', newAccess);
      return getReports(newAccess, true);
    }

    if (!res.ok || data === undefined) {
      return false;
    }

    const grouped = groupReports(data);
    setGroups(grouped);
    setSelected(new Set());
    onHasReports(grouped.length > 0);
    return true;
  }, []);

  // Not with an empty token: that is the sign-off, not a new session.
  useEffect(() => {
    if (accessToken && askedWith.current !== accessToken) {
      getReports(accessToken);
    }
  }, [accessToken, getReports]);

  // Filtering a CNC answer means walking tens of thousands of names, so the
  // box waits for a pause instead of doing it on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed), 250);
    return () => clearTimeout(timer);
  }, [typed]);

  // A month of a busy organisation can hold hundreds of files; it shows this
  // many until "Ver más".
  const FILES_AT_A_TIME = 50;

  // A search shows at most this many files, however many match: "2026-" is
  // most of a CNC answer.
  const RESULTS_AT_A_TIME = 200;

  const searching = query.trim().length > 0;
  const filtering = searching || onlySelected;

  // While searching, what matches is shown wherever it is, without asking
  // the user to open the organisation and the month first. The filter runs
  // when the query or the reports change, not on every render: a click used
  // to walk every name again.
  // Recomputed when the query, the reports or, while showing the selection,
  // the selection change: with tens of thousands of names it must not run on
  // every render.
  const selectionFilter = onlySelected ? selected : null;
  const { visibleGroups, matchCount, shownCount } = useMemo(() => {
    if (!filtering) {
      return { visibleGroups: groups, matchCount: 0, shownCount: 0 };
    }

    const wanted = query.trim().toLowerCase();
    let found = 0;
    let shown = 0;
    const filtered = groups
      .map((group) => ({
        ...group,
        months: group.months
          .map((month) => {
            const files = month.files.filter((file) =>
              (!selectionFilter || selectionFilter.has(file.url))
              && (!wanted || file.name.toLowerCase().includes(wanted)));
            found += files.length;
            const room = Math.max(RESULTS_AT_A_TIME - shown, 0);
            shown += Math.min(files.length, room);
            // files is what is drawn; matching is what the month's label
            // counts and what its checkbox takes, even past the 200 shown.
            return { ...month, files: files.slice(0, room), matching: files, matches: files.length };
          })
          .filter((month) => month.matches > 0),
      }))
      .filter((group) => group.months.length > 0);

    return { visibleGroups: filtered, matchCount: found, shownCount: shown };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, query, filtering, selectionFilter]);

  const isOpen = (id) => filtering || openSections.includes(id);

  // While searching everything is open by definition, so the same button
  // also clears the search; otherwise pressing it did nothing visible.
  // The search and the selection view narrow the list together, and each is
  // undone on its own: this button clears the search (or closes what is
  // open), and the count beside the buttons leaves the selection view.
  const clearSearch = () => {
    setTyped('');
    setQuery('');
  };

  const collapseAll = () => {
    setOpenSections([]);
    setFullMonths([]);
  };

  const toggleSection = (id) => {
    setOpenSections((open) => open.includes(id) ? open.filter((o) => o !== id) : [...open, id]);
  };

  // The cap holds while searching too: a date matches thousands of names in
  // a CNC answer, and drawing them all froze the panel.
  // While searching, month.files holds what fits in the 200 shown; matches
  // is how many there really are, which is what the label must say.
  const countOf = (month) => month.matches === undefined ? month.files.length : month.matches;

  const filesToSelect = (month) => month.matching === undefined ? month.files : month.matching;

  const filesOf = (month) => fullMonths.includes(month.id)
    ? month.files
    : month.files.slice(0, FILES_AT_A_TIME);

  const isSelected = (url) => selected.has(url);

  const toggleFile = (url) => {
    setSelected((current) => {
      const next = new Set(current);
      if (!next.delete(url)) {
        next.add(url);
      }
      // Nothing selected left to show.
      if (next.size === 0) {
        setOnlySelected(false);
      }
      return next;
    });
  };

  // The checkbox of a month takes its files with it, which is what it looked
  // like it did: it had no handler at all.
  // A month whose matches are past the 200 shown has none of them on
  // screen, so its checkbox works on what is shown, as its files do.
  const toggleMonth = (month) => {
    const files = filesToSelect(month);
    setSelected((current) => {
      const next = new Set(current);
      const allSelected = files.every((file) => current.has(file.url));
      files.forEach((file) => allSelected ? next.delete(file.url) : next.add(file.url));
      return next;
    });
  };

  const monthState = (month) => {
    const files = filesToSelect(month);
    let selectedFiles = 0;
    files.forEach((file) => { if (selected.has(file.url)) selectedFiles += 1; });
    if (selectedFiles === 0) return 'none';
    return selectedFiles === files.length ? 'all' : 'some';
  };

  const show = () => setVisible(true);

  // Closing the panel leaves it as it was found: nothing open, nothing
  // selected and no search, whether it is closed with CANCELAR or by
  // clicking outside.
  const hide = () => {
    setVisible(false);
    setOpenSections([]);
    setFullMonths([]);
    setSelected(new Set());
    setTyped('');
    setQuery('');
    setOnlySelected(false);
  };

  // Waits for the answer instead of hiding the panel and guessing with
  // timers, which used to leave the old list on screen.
  const refresh = async () => {
    setShowLoader(true);
    const ok = await getReports(accessToken);
    setShowLoader(false);
    if (!ok) {
      alert("No fue posible actualizar los reportes. Intente de nuevo.", "danger");
    }
  };

  // The back signs one report at a time, for fifteen minutes
  // (GET /users/reports/<id>/signed-url, back-ratings #23). The signed URL
  // is asked for just before the file and never kept. documento is not
  // fetched any more: once the back closes the objects it will not open.
  const signedUrl = async (file) => {
    const ask = (access) => fetch(`${API_DEFAULT}/${API.signedUrl(file.id)}`, {
      headers: { "Authorization": "Bearer " + access }
    });

    let res = await ask(latestProps.current.accessToken);
    if (res.status === 401) {
      const data = await res.json().catch(() => undefined);
      if (!data || data['code'] !== 'token_not_valid') {
        throw new Error(`${file.name}: 401`);
      }
      const { setAccessToken, getAccessTokenWithRefresh } = latestProps.current;
      const newAccess = await getAccessTokenWithRefresh();
      if (!newAccess) {
        throw new Error(`${file.name}: session`);
      }
      setAccessToken(newAccess);
      localStorage.setItem('accessToken', newAccess);
      latestProps.current.accessToken = newAccess;
      res = await ask(newAccess);
    }

    // 404 (gone, or not this user's) and 503 (the signature failed) count as
    // a file that could not be downloaded, like a failed fetch.
    if (!res.ok) {
      throw new Error(`${file.name}: ${res.status}`);
    }
    const data = await res.json();
    return data['url'];
  };

  const fetchFile = async (file) => {
    const res = await fetch(await signedUrl(file));
    if (!res.ok) {
      throw new Error(`${file.name}: ${res.status}`);
    }
    return res.blob();
  };

  // One file or a zip, both fetched the same way: an <a download> pointing at
  // the bucket is ignored across origins, so the browser navigated to the
  // file instead of saving it.
  const download = async () => {
    if (selected.size === 0) {
      alert("Debe seleccionar al menos un reporte", "danger");
      return;
    }

    const files = groups
      .flatMap((group) => group.months)
      .flatMap((month) => month.files)
      .filter((file) => selected.has(file.url));

    setDownloading(true);
    setShowLoader(true);

    // Five at a time rather than one after another: a month of a busy
    // organisation is dozens of files. One that is no longer in the bucket
    // does not stop the rest; what failed is counted and said.
    const results = new Array(files.length);
    let next = 0;
    const worker = async () => {
      while (next < files.length) {
        const index = next;
        next += 1;
        try {
          results[index] = { file: files[index], blob: await fetchFile(files[index]) };
        } catch (e) {
          results[index] = undefined;
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(5, files.length) }, worker));

    const blobs = results.filter(Boolean);
    let failed = files.length - blobs.length;

    try {
      if (blobs.length === 1) {
        FileSaver.saveAs(blobs[0].blob, blobs[0].file.name);
      } else if (blobs.length > 1) {
        const zip = JsZip();
        blobs.forEach(({ file, blob }) => zip.file(file.name, blob));
        FileSaver.saveAs(await zip.generateAsync({ type: 'blob' }), 'Reportes.zip');
      }
    } catch (e) {
      failed = files.length;
      blobs.length = 0;
    }

    if (blobs.length > 0) {
      setSelected(new Set());
    }
    if (failed > 0) {
      alert(blobs.length === 0
        ? (files.length === 1
          ? "No fue posible descargar el reporte: puede que el archivo ya no esté disponible."
          : `No fue posible descargar ninguno de los ${files.length} reportes: puede que los archivos ya no estén disponibles.`)
        : `Se descargaron ${blobs.length} de ${files.length} reportes; ${failed} no están disponibles.`,
        "danger");
    }
    setShowLoader(false);
    setDownloading(false);
  };

  useImperativeHandle(ref, () => ({ show }));

  // A month that holds Modelo 2 shows its files, and the rest of the month
  // behind "Otros reportes", a section of its own that opens, closes and is
  // selected like a month (the owner's decision of 2026-09-30). A month
  // without Modelo 2, or with nothing else, is drawn whole, as before it.
  // The month's own checkbox, count and download still take every file.
  // Only an open month is split, so the tens of thousands of files of CNC
  // are not walked.
  const splitMonth = (month) => {
    const modelo2 = month.files.filter((file) => file.modelo2);
    if (modelo2.length === 0 || modelo2.length === month.files.length) {
      return { main: month, others: null };
    }
    const others = month.files.filter((file) => !file.modelo2);
    const othersMatching = month.matching === undefined ? undefined : month.matching.filter((file) => !file.modelo2);
    return {
      main: { ...month, files: modelo2 },
      others: {
        id: `${month.id}-others`,
        label: 'Otros reportes',
        files: others,
        matching: othersMatching,
        matches: othersMatching === undefined ? undefined : othersMatching.length,
      },
    };
  };

  // The files of an open month, or of its "Otros reportes", up to "Ver más".
  const renderFiles = (month) => (
    <>
      {filesOf(month).map((file) => (
        <div key={file.url} className={ReportStyle["report-days-group"]}>
          <input
            type="checkbox"
            id={file.url}
            className="input-report"
            name="checkbox"
            checked={isSelected(file.url)}
            onChange={() => toggleFile(file.url)}
          />
          <label htmlFor={file.url}>{file.name}</label>
        </div>
      ))}
      {filesOf(month).length < month.files.length ? (
        <div className={ReportStyle["report-days-group"]}>
          <button type="button" className={ReportStyle["show-more"]} onClick={() => setFullMonths((current) => [...current, month.id])}>
            {`Ver más (${month.files.length - filesOf(month).length})`}
          </button>
        </div>
      ) : null}
    </>
  );

  // What an open month draws: its files, then its "Otros reportes" section.
  const renderMonthContent = (month) => {
    const { main, others } = splitMonth(month);
    return (
      <>
        {renderFiles(main)}
        {others ? renderMonth(others) : null}
      </>
    );
  };

  // The stylesheet decides what is visible: .report-group-container is
  // height zero until it carries .active, and it wraps an organisation's
  // months (for a client there is no organisation header, so it is always
  // open). A month's files live in .report-days-container, active while the
  // month is open.
  // The same header and container draw a month and its "Otros reportes".
  function renderMonth(month) {
    return (
    <React.Fragment key={month.id}>

      <div className={ReportStyle["report-month-container"]}>
        <div className={ReportStyle["layer-month-container"]} onClick={() => toggleSection(month.id)}></div>
        <input
          type="checkbox"
          id={month.id}
          name="checkbox"
          checked={monthState(month) === 'all'}
          ref={(input) => { if (input) input.indeterminate = monthState(month) === 'some'; }}
          onChange={() => toggleMonth(month)}
        />
        <label htmlFor={month.id}> {`${month.label} · ${countOf(month)} ${countOf(month) === 1 ? 'archivo' : 'archivos'}`} </label>
        <div className={`${ReportStyle["month-arrow-icon"]}${isOpen(month.id) ? ` ${ReportStyle["icon-rotate"]}` : ''}`}>
          <ion-icon name="chevron-down-outline"></ion-icon>
        </div>
      </div>

      {/* A closed month draws no files: with tens of thousands of them,
          drawing the lot is what froze the panel. */}
      <div className={`${ReportStyle["report-days-container"]}${isOpen(month.id) ? ` ${ReportStyle["active"]}` : ''}`}>
        {isOpen(month.id) ? renderMonthContent(month) : null}
      </div>

    </React.Fragment>
    );
  }

  // A client has no organisation to open, so its container is given the
  // height the old panel set inline, rather than .active, which also draws
  // the line under an organisation's months.
  const renderMonths = (group) => {
    const forClient = group.organisation === null;
    const open = forClient || isOpen(group.id);
    return (
      <div
        className={`${ReportStyle["report-group-container"]}${open && !forClient ? ` ${ReportStyle["active"]}` : ''}`}
        style={forClient ? { height: 'auto' } : undefined}
      >
        {open ? group.months.map(renderMonth) : null}
      </div>
    );
  };

  return (
    <div className={`${ReportStyle["report"]}${visible ? ` ${ReportStyle["show-report"]}` : ` ${ReportStyle["hide-report"]}`}`} id="report">
      <div className={ReportStyle["layer-hide-menu"]} onClick={() => hide()}></div>
      <div className={ReportStyle["report-container"]} id="report-container">
        <div className={ReportStyle["report-header"]}>
          <h2>Seleccione el reporte que desea descargar</h2>
        </div>
        <div className={ReportStyle["report-search"]}>
          <input
            type="search"
            id="report-search"
            placeholder="Buscar por nombre de archivo"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
          />
          {searching || openSections.length > 0 ? (
            <button type="button" className={ReportStyle["collapse-all"]} onClick={() => searching ? clearSearch() : collapseAll()}>
              {searching ? 'Limpiar búsqueda' : 'Colapsar todo'}
            </button>
          ) : null}
        </div>
        {filtering && matchCount === 0 ? (
          <div className={ReportStyle["report-results"]}>
            {onlySelected && searching
              ? 'Ninguno de los reportes seleccionados coincide con la búsqueda.'
              : 'Ningún reporte coincide con la búsqueda.'}
          </div>
        ) : null}
        {filtering && matchCount > shownCount ? (
          <div className={ReportStyle["report-results"]}>
            {`Se muestran ${shownCount} de ${matchCount} ${onlySelected ? 'seleccionados' : 'resultados'}. Afine la búsqueda para ver el resto.`}
          </div>
        ) : null}
        <div className={ReportStyle["reports-container-scroll"]}>
          {visibleGroups.map((group) => (
            <React.Fragment key={group.id}>
              {group.organisation === null ? null : (
                <div
                  className={`${ReportStyle["name-container"]}${isOpen(group.id) ? ` ${ReportStyle["active"]}` : ''}`}
                  onClick={() => toggleSection(group.id)}
                >
                  <h4>{group.organisation}</h4>
                  <div className={`${ReportStyle["organization-arrow-icon"]}${isOpen(group.id) ? ` ${ReportStyle["icon-rotate"]}` : ''}`}>
                    <ion-icon name="chevron-down-outline"></ion-icon>
                  </div>
                </div>
              )}
              {renderMonths(group)}
            </React.Fragment>
          ))}
        </div>

        <div className={ReportStyle["report-button-container"]}>
          <div className={ReportStyle["update-status"]} onClick={() => refresh()}>
            <ion-icon name="reload-outline"></ion-icon>
            <span>Actualizar</span>
          </div>
          {selected.size > 0 ? (
            <div className={ReportStyle["report-selection"]}>
              <button
                type="button"
                className={`${ReportStyle["show-selection"]}${onlySelected ? ` ${ReportStyle["active"]}` : ''}`}
                onClick={() => setOnlySelected(!onlySelected)}
              >
                {onlySelected
                  ? 'Ver todos'
                  : `${selected.size} ${selected.size === 1 ? 'seleccionado' : 'seleccionados'}`}
              </button>
              <button type="button" className={ReportStyle["clear-selection"]} onClick={() => { setSelected(new Set()); setOnlySelected(false); }}>Quitar selección</button>
            </div>
          ) : null}
          <button className={ReportStyle["report-button-cancel"]} onClick={() => hide()}>CANCELAR</button>
          <button id="reports-download" className={ReportStyle["report-button-download"]} disabled={downloading} onClick={() => download()}>DESCARGAR</button>
        </div>
      </div>
    </div>
  );
});

export default Reports;
